/**
 * useWatchButtonState
 *
 * Centralized hook that determines the WATCH button state for any event/channel
 * across the entire BKFC web site. Encapsulates the full decision logic:
 *
 *   1. Extracts event timestamp and streamUrl from channel + slim CMS data
 *   2. Reads live status from the backend (via useLiveEventStatus): the
 *      never-cached hide_countdown, the start_time/event_date override, and
 *      is_live / stream_available (server-side Gigcasters probe — no client HLS poll)
 *   3. Produces a single `buttonState` value:
 *      - 'watch-now'   → Stream is available OR hide_countdown is set OR event is in the past
 *      - 'watch-in'    → Event is in the future, stream is down, countdown visible
 *      - 'buy-now'     → Subscription paywall (not unlocked)
 *      - 'unavailable' → No data / not playable
 *
 * Usage:
 *   const { buttonState, countdown, isLive, checkNow } = useWatchButtonState(channel, slimEventData);
 */

import { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { useLiveEventStatus } from './useLiveEventStatus';
import { deriveEventLifecycle, computeEndBoundary, LifecycleState } from '../utils/eventLifecycle';
import { getChannelEventDateValue } from '../utils/channelDateUtils';
import cmsService, { extractGigcasterIdFromChannel } from '../services/CMSService';

const STREAM_URL_KEYS = ['video_m3u8', 'stream_url', 'hls', 'm3u8', 'live_url'];

function extractStreamUrl(channel) {
  if (!channel) return null;

  if (channel.video_m3u8 && typeof channel.video_m3u8 === 'string') {
    return channel.video_m3u8;
  }

  const firstVideo =
    channel.spltVideos?.[0] ??
    channel.playlist?.[0] ??
    channel.contents?.[0] ??
    channel.channels?.[0];
  if (firstVideo?.video_m3u8 && typeof firstVideo.video_m3u8 === 'string') {
    return firstVideo.video_m3u8;
  }

  if (channel.video?.video_m3u8 && typeof channel.video.video_m3u8 === 'string') {
    return channel.video.video_m3u8;
  }

  const fields = channel.custom_fields;
  if (fields) {
    if (Array.isArray(fields)) {
      const match = fields.find(f => STREAM_URL_KEYS.includes(f.field_title));
      if (match?.field_value) return match.field_value;
    } else if (typeof fields === 'object') {
      for (const key of STREAM_URL_KEYS) {
        if (fields[key]) return fields[key];
      }
    }
  }

  return null;
}

function parseTruthyFlag(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    return ['true', '1', 'yes', 'y'].includes(value.trim().toLowerCase());
  }
  return false;
}

function getHideCountdown(slimEventData, channel) {
  if (slimEventData?.hide_countdown !== undefined) {
    return parseTruthyFlag(slimEventData.hide_countdown);
  }

  const fields = channel?.custom_fields;
  if (!fields) return false;

  if (Array.isArray(fields)) {
    const field = fields.find(
      f => (f?.field_title || f?.name || f?.key || '').trim().toLowerCase() === 'hide_countdown',
    );
    return field ? parseTruthyFlag(field.field_value ?? field.value) : false;
  }

  if (typeof fields === 'object' && fields.hide_countdown !== undefined) {
    return parseTruthyFlag(fields.hide_countdown);
  }

  return false;
}

function parseTimestamp(rawDate) {
  if (!rawDate) return null;
  const parsed = moment.utc(rawDate);
  return parsed.isValid() ? parsed.valueOf() : null;
}

function getEventTimestamp(channel, slimEventData) {
  return parseTimestamp(getChannelEventDateValue(channel, slimEventData));
}

function calculateCountdown(timestamp) {
  if (!timestamp) return null;

  const now = Date.now();
  const diff = timestamp - now;

  if (diff <= 0) return null;

  const duration = moment.duration(diff);
  const days = Math.floor(duration.asDays());
  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  return {
    days: String(days).padStart(2, '0'),
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0'),
    totalMs: diff,
  };
}

/**
 * @param {object} channel - DotStudioPro channel object
 * @param {object|null} slimEventData - CMS slim event data (from cmsService.getSlimEventData)
 * @returns {object} Watch button state
 */
export function useWatchButtonState(channel, slimEventData = null) {
  const streamUrl = extractStreamUrl(channel);

  // Never-cached live status overrides the cached CMS values so a last-minute
  // reschedule / hidden countdown / corrected date takes effect immediately.
  const slug = channel?.slug || channel?.strSlug;
  const gigcasterId =
    extractGigcasterIdFromChannel(channel) ||
    cmsService.getCachedGigcasterIdForSlug?.(slug) ||
    null;
  const live = useLiveEventStatus(gigcasterId);

  const hideCountdown = live.hideCountdown ?? getHideCountdown(slimEventData, channel);
  const liveTimestamp = parseTimestamp(live.effectiveDate);
  // start_time ?? event_date (live) wins; fall back to cached CMS while loading.
  const eventTimestamp = liveTimestamp ?? getEventTimestamp(channel, slimEventData);
  const isFuture = eventTimestamp ? eventTimestamp > Date.now() : false;
  const isSubscriptionLocked = channel?.subscription_access?.unlocked === false;

  // Liveness comes from the backend Gigcasters probe (via live), not a client
  // HLS probe — it reflects the actual full-event encoder state and isn't subject
  // to device geo/edge blocks.
  const [countdown, setCountdown] = useState(() => calculateCountdown(eventTimestamp));
  const countdownIntervalRef = useRef(null);

  useEffect(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    if (!eventTimestamp || eventTimestamp <= Date.now()) {
      setCountdown(null);
      return;
    }

    setCountdown(calculateCountdown(eventTimestamp));
    countdownIntervalRef.current = setInterval(() => {
      const remaining = calculateCountdown(eventTimestamp);
      setCountdown(remaining);
      if (!remaining) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        live.refresh();
      }
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [eventTimestamp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lifecycle is the single decision: upcoming → starting-soon → live → replay → ended.
  const endBoundaryMs = computeEndBoundary(eventTimestamp, parseTimestamp(live.endTime));
  const lifecycle = deriveEventLifecycle({
    effectiveStartMs: eventTimestamp,
    endBoundaryMs,
    streamState: live.streamState,
    hideCountdown,
    isLocked: isSubscriptionLocked,
  });

  let buttonState;
  switch (lifecycle) {
    case LifecycleState.BUY_NOW:
      buttonState = 'buy-now';
      break;
    case LifecycleState.UPCOMING:
      buttonState = 'watch-in'; // countdown
      break;
    case LifecycleState.STARTING_SOON:
      buttonState = 'starting-soon';
      break;
    default:
      buttonState = 'watch-now'; // LIVE, REPLAY, WATCH_NOW(hide_countdown), ENDED→DSP replay
  }

  return {
    buttonState,
    lifecycle,
    countdown,
    isLive: live.isLive,
    isVod: live.streamState === 'vod',
    isAvailable: live.streamAvailable,
    isChecking: !live.loaded,
    isFuture,
    hideCountdown,
    streamUrl,
    eventTimestamp,
    /** Live start_time ?? event_date (raw string), for displaying the date */
    effectiveDate: live.effectiveDate ?? getChannelEventDateValue(channel, slimEventData),
    refresh: live.refresh,
  };
}

export default useWatchButtonState;
